import React, { useMemo } from 'react'
import { Column } from 'react-table'

import { CustomerOrderTag } from '../../../../../graphql/camel'
import { isNotNull } from '../../../../../utils/collectionTools'
import { Chip, ChipColor } from '../../../../Chip'
import { RowAsideProps, TableProps } from '../../../../Table'
import {
  customerNameColumn,
  dateColumn,
  orderNumberColumn,
  statusColumn,
  totalColumn,
  trackingColumn,
} from './columns'
import { LinkToOrder } from './LinkToOrder'
import { StyledTable } from './StyledTable'
import { TableDataRewardsOrder } from './types'
import { withUrlFragment } from './withUrlFragment'

export const orderTagLabels: Record<CustomerOrderTag, string> = {
  AUTO_SHIP: 'Auto-Ship',
  GENERATED_LEAD: 'Patient Referral Lead',
}

const chipDetails = (
  isNotConsulted: boolean,
  tag: CustomerOrderTag,
): { color: ChipColor; label: string } => {
  if (tag === 'GENERATED_LEAD') {
    return {
      color: isNotConsulted ? 'red' : 'green',
      label: isNotConsulted ? orderTagLabels[tag] : 'Consulted Referral',
    }
  }
  return {
    color: 'blue',
    label: orderTagLabels[tag],
  }
}

const columnsForCustomerRewardsOrders = (urlFragment: string): Column<TableDataRewardsOrder>[] => [
  dateColumn(urlFragment),
  orderNumberColumn(urlFragment),
  customerNameColumn(urlFragment),
  totalColumn(urlFragment),
  statusColumn(urlFragment),
  trackingColumn(urlFragment),
]

const CustomerRewardsOrdersRowAside: React.VFC<
  RowAsideProps<TableDataRewardsOrder> & { urlFragment: string }
> = ({ row, urlFragment }) => {
  const { notConsulted, tags } = row.original
  return tags && tags.length > 0 ? (
    <LinkToOrder orderId={row.original.orderId} urlFragment={urlFragment}>
      {tags.filter(isNotNull).map((tag) => {
        const { color, label } = chipDetails(Boolean(notConsulted), tag)
        return <Chip color={color} key={label} label={label} />
      })}
    </LinkToOrder>
  ) : null
}

export interface CustomerRewardsOrdersTableProps {
  className?: string
  urlFragment: string
  orders: TableDataRewardsOrder[]
}

export const CustomerRewardsOrdersTable: React.VFC<CustomerRewardsOrdersTableProps> = ({
  className,
  orders,
  urlFragment,
}) => {
  const columns = useMemo(() => columnsForCustomerRewardsOrders(urlFragment), [urlFragment])
  const rowAside = useMemo(
    () => withUrlFragment(CustomerRewardsOrdersRowAside, urlFragment),
    [urlFragment],
  )

  const initialSortBy = useMemo(
    () => [
      {
        desc: true,
        id: 'date',
      },
    ],
    [],
  )

  return orders.length ? (
    <StyledTable<React.FC<TableProps<TableDataRewardsOrder>>>
      className={className}
      columns={columns}
      data={orders}
      initialSortBy={initialSortBy}
      rowAside={rowAside}
      showHeaders
    />
  ) : null
}
